/**
 * LeadsToolbarV2 - Inline filter bar (NoBroker-style)
 *
 * Row 1: DateRangeFilter
 * Row 2: [Budget v] [Location v] [Property Type v] [Intent v] [More Filters] [Pagination]
 * Row 3: Last Contacted — hour badges apply immediately; Custom uses Apply; use Reset Filter to clear
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Typography,
  Badge,
  Tooltip,
  Chip,
  Autocomplete,
  TextField,
} from '@mui/material';
import {
  FilterList as FilterListIcon,
  ArrowBack as ArrowBackIcon,
  RestartAlt as RestartAltIcon,
  BookmarkBorder as BookmarkBorderIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import Pagination from '../../../components/common/Pagination';
import DateRangeFilter from '../../../components/common/DateRangeFilter';

// Keys owned by LeadFilters.js (More Filters panel) — only these count toward the badge
const MORE_FILTER_KEYS = ['agent_id', 'agents', 'assigned_to', 'source', 'channel_partner', 'channel_partners', 'lead_urgency', 'lead_lcp', 'tags', 'site_visit_frequency'];

const INLINE_FILTERS = [
  { key: 'budget_ranges', label: 'Budget', placeholder: 'Select budget' },
  { key: 'locations', label: 'Location', placeholder: 'Select location' },
  { key: 'property_types', label: 'Property Type', placeholder: 'Select Property Type' },
  { key: 'intent', label: 'Intent', placeholder: 'Select intent' },
];

const LAST_CONTACTED_PRESETS = [
  { key: '1hour', label: '1 hours', hours: 1 },
  { key: '6hours', label: '6 hours', hours: 6 },
  { key: '12hours', label: '12 hours', hours: 12 },
  { key: '24hours', label: '24 hours', hours: 24 },
  { key: 'custom', label: 'Custom' },
];

/** Matches toolbar chips / Apply payload — used to detect "applied" and show Clear. */
function getAppliedLastContactedPresetKey(appliedFilters) {
  const lc = appliedFilters?.last_contacted;
  if (!lc || typeof lc !== 'object') return '';
  if (lc.preset) return lc.preset;
  const hasRange =
    lc.start_date ||
    lc.end_date ||
    lc.start_time ||
    lc.end_time ||
    lc.from_date ||
    lc.to_date;
  return hasRange ? 'custom' : '';
}

/** Custom paper for inline filter dropdowns — adds Select All / Clear header */
const FilterDropdownPaper = React.forwardRef(
  ({ onClear, hasSelection, children, ...paperProps }, ref) => (
    <Paper ref={ref} {...paperProps}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, py: 0.5, borderBottom: '1px solid #e5e7eb' }}>
        <Button
          size="small"
          variant="text"
          onMouseDown={(e) => { e.preventDefault(); onClear?.(); }}
          disabled={!hasSelection}
          sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#DC2626', textTransform: 'none', px: 0.75, py: 0.25, minWidth: 0, '&:hover': { bgcolor: '#FEF2F2' }, '&.Mui-disabled': { color: '#d1d5db' } }}
        >
          Clear
        </Button>
      </Box>
      {children}
    </Paper>
  )
);

const dropdownSx = {
  flex: '1 1 0',
  minWidth: 70,
  '& .MuiInputBase-root': {
    fontSize: '0.72rem',
    minHeight: '30px',
    height: '30px',
    borderRadius: '6px',
    bgcolor: '#fff',
    flexWrap: 'nowrap',
    paddingRight: '52px !important',
  },
  '& .MuiInputBase-input': {
    fontSize: '0.72rem',
    padding: '2px 4px !important',
    minWidth: '0 !important',
    flex: '1 1 0',
  },
  '& .MuiOutlinedInput-root': {
    flexWrap: 'nowrap',
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#0A6B3E',
      boxShadow: '0 0 0 3px rgba(10,107,62,0.12)',
    },
  },
  '& .MuiAutocomplete-tag': { flexShrink: 0, maxWidth: 'calc(100% - 8px)' },
  '& .MuiAutocomplete-endAdornment': { top: '50%', transform: 'translateY(-50%)' },
  '& .MuiAutocomplete-clearIndicator': { padding: '1px', '& svg': { fontSize: '14px' } },
  '& .MuiAutocomplete-popupIndicator': { padding: '1px', '& svg': { fontSize: '16px' } },
};

const chipSx = {
  height: '20px',
  borderRadius: '5px',
  fontSize: '0.68rem',
  fontWeight: 600,
  bgcolor: '#059669',
  color: '#fff',
  '& .MuiChip-deleteIcon': { fontSize: '0.75rem', color: '#fff', opacity: 0.7, '&:hover': { opacity: 1 } },
  '& .MuiChip-label': { px: 0.75 },
};

const LeadsToolbarV2 = ({
  onFilterClick,
  onBack,
  appliedFilters = {},
  onFiltersChange,
  onResetFilters,
  defaultFilters = {},
  page = 1,
  rowsPerPage = 50,
  onPageChange,
  onRowsPerPageChange,
  paginationInfo = {},
  isCustomFetchMode = false,
  customFetchTitle = '',
  dateRangeState,
  onDateRangeChange,
  /** Incrementing id opens save-search dialog (LeadFilters) — saves current applied filters */
  onSaveSearchClick,
  /** Same rules as Save inside filter panel */
  canSaveSearch = false,
  searchValue,
  onSearchChange,
}) => {
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [localSearch, setLocalSearch] = useState(searchValue || '');
  const searchTimeoutRef = useRef(null);

  useEffect(() => { setLocalSearch(searchValue || ''); }, [searchValue]);

  const handleSearchInput = (e) => {
    const value = e.target.value;
    setLocalSearch(value);
    clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      onSearchChange?.(value);
    }, 500);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      clearTimeout(searchTimeoutRef.current);
      onSearchChange?.(localSearch);
    }
  };
  /** Only 'custom' is pending; hour presets apply on click (no Apply). */
  const [pendingPreset, setPendingPreset] = useState('');

  const activeLastContacted = getAppliedLastContactedPresetKey(appliedFilters);
  const customPending = pendingPreset === 'custom';

  const prevLastContactedRef = useRef(appliedFilters?.last_contacted);

  // Clear local Custom UI only when an applied last_contacted was removed (Reset Filter, chip toggle, etc.).
  // Do not clear when last_contacted was never set — otherwise opening Custom (pending) would be wiped every render.
  useEffect(() => {
    const cur = appliedFilters?.last_contacted;
    const prev = prevLastContactedRef.current;
    prevLastContactedRef.current = cur;

    const hadApplied = getAppliedLastContactedPresetKey({ last_contacted: prev }) !== '';
    const hasApplied = getAppliedLastContactedPresetKey({ last_contacted: cur }) !== '';
    if (hadApplied && !hasApplied) {
      setPendingPreset('');
      setCustomStart(null);
      setCustomEnd(null);
    }
  }, [appliedFilters?.last_contacted]);

  const moreFiltersCount = MORE_FILTER_KEYS.filter((key) => {
    const v = appliedFilters[key];
    if (v == null) return false;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'object') return Object.keys(v).length > 0;
    return v !== '';
  }).length;

  const hasAnyFilter = Object.keys(appliedFilters).some((key) => {
    if (key === 'is_deleted') return false;
    const v = appliedFilters[key];
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'object' && v !== null) return Object.keys(v).length > 0;
    return v != null && v !== '';
  });

  /** Show Reset Filter for applied filters or while Custom Last Contacted date UI is open */
  const showResetFilterSlot = hasAnyFilter || customPending;

  const handleResetFiltersClick = useCallback(() => {
    setPendingPreset('');
    setCustomStart(null);
    setCustomEnd(null);
    prevLastContactedRef.current = undefined;
    onResetFilters?.();
  }, [onResetFilters]);

  const handleInlineFilterChange = useCallback(
    (filterKey, newValue) => {
      const updated = { ...appliedFilters };

      if (filterKey === 'budget_ranges') {
        // Convert selected range options to a numeric { min, max } budget object
        if (!newValue || newValue.length === 0) {
          delete updated.budget_ranges;
          delete updated.budget;
        } else {
          updated.budget_ranges = newValue.map((item) =>
            typeof item === 'object' && item?.value !== undefined ? item.value : item,
          );
          // Resolve full option objects (items may already be objects or just value strings)
          const opts = newValue.map((item) =>
            typeof item === 'object' && item?.min !== undefined
              ? item
              : (defaultFilters?.budget_ranges || []).find((o) => o.value === item),
          ).filter(Boolean);

          const mins = opts.map((o) => o.min).filter((m) => m != null);
          const maxes = opts.map((o) => o.max).filter((m) => m != null);
          updated.budget = {
            min: mins.length > 0 ? Math.min(...mins) : null,
            max: maxes.length > 0 ? Math.max(...maxes) : null,
          };
        }
        onFiltersChange(updated);
        return;
      }

      const values = (newValue || []).map((item) =>
        typeof item === 'object' && item?.value !== undefined ? item.value : item,
      );
      updated[filterKey] = values;
      if (values.length === 0) delete updated[filterKey];
      onFiltersChange(updated);
    },
    [appliedFilters, onFiltersChange, defaultFilters],
  );

  const handleLastContactedSelect = useCallback(
    (presetKey) => {
      const appliedKey = getAppliedLastContactedPresetKey(appliedFilters);

      // ── Custom: open date UI; second click closes editor without applying
      if (presetKey === 'custom') {
        if (customPending) {
          setPendingPreset('');
          setCustomStart(null);
          setCustomEnd(null);
          return;
        }
        setPendingPreset('custom');
        const lc = appliedFilters?.last_contacted;
        if (appliedKey === 'custom' && lc && typeof lc === 'object') {
          const s = lc.start_date || lc.start_time;
          const e = lc.end_date || lc.end_time;
          if (s) setCustomStart(dayjs(s));
          if (e) setCustomEnd(dayjs(e));
        }
        return;
      }

      // ── Hour presets (1h–24h): apply immediately; same chip again clears
      setPendingPreset('');
      setCustomStart(null);
      setCustomEnd(null);

      if (!customPending && appliedKey === presetKey) {
        const updated = { ...appliedFilters };
        delete updated.last_contacted;
        onFiltersChange(updated);
        return;
      }

      const preset = LAST_CONTACTED_PRESETS.find((p) => p.key === presetKey);
      if (!preset || preset.key === 'custom' || preset.hours == null) return;

      const now = dayjs();
      onFiltersChange({
        ...appliedFilters,
        last_contacted: {
          preset: presetKey,
          start_date: now.subtract(preset.hours, 'hour').format('YYYY-MM-DD HH:mm'),
          end_date: now.format('YYYY-MM-DD HH:mm'),
        },
      });
    },
    [appliedFilters, onFiltersChange, customPending],
  );

  const handleLastContactedApply = useCallback(() => {
    if (!customPending || !customStart || !customEnd) return;
    onFiltersChange({
      ...appliedFilters,
      last_contacted: {
        preset: 'custom',
        start_date: customStart.format('YYYY-MM-DD HH:mm'),
        end_date: customEnd.format('YYYY-MM-DD HH:mm'),
      },
    });
    setPendingPreset('');
  }, [customPending, customStart, customEnd, appliedFilters, onFiltersChange]);

  const handleClearFilter = useCallback((filterKey) => {
    const updated = { ...appliedFilters };
    delete updated[filterKey];
    if (filterKey === 'budget_ranges') delete updated.budget;
    onFiltersChange(updated);
  }, [appliedFilters, onFiltersChange]);

  const getSelectedValues = (filterKey) => {
    const raw = appliedFilters?.[filterKey];
    if (!Array.isArray(raw) || raw.length === 0) return [];
    const options = defaultFilters?.[filterKey];
    if (!Array.isArray(options)) return raw;
    return raw.map((v) => {
      if (typeof v === 'object' && v !== null) return v;
      const match = options.find((o) => (o?.value ?? o) === v);
      return match ?? v;
    });
  };

  return (
    <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
      {/* Row 1 - Date Range */}
      <Box sx={{ px: 1.5, pt: 1.5, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {isCustomFetchMode && onBack && (
            <IconButton onClick={onBack} size="small">
              <ArrowBackIcon />
            </IconButton>
          )}
          {isCustomFetchMode && (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {customFetchTitle}
              </Typography>
              {dateRangeState && (
                <Typography variant="caption" color="text.secondary">
                  {dateRangeState.currentStart?.format('MMM DD, YYYY')} -{' '}
                  {dateRangeState.currentEnd?.format('MMM DD, YYYY')}
                </Typography>
              )}
            </Box>
          )}
        </Box>
        {!isCustomFetchMode && (
          <DateRangeFilter onChange={onDateRangeChange} initialDateRange={dateRangeState} />
        )}
      </Box>

      {/* Row 2 - Inline Filter Dropdowns + More Filters + Save Search + Pagination */}
      <Box
        sx={{
          px: 1.5,
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'nowrap',
          minWidth: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: '0 0 80%', minWidth: 0, overflow: 'hidden' }}>
        {INLINE_FILTERS.map(({ key, label, placeholder }) => {
          const options = defaultFilters?.[key] || [];
          const value = getSelectedValues(key);
          return (
            <Autocomplete
              key={key}
              multiple
              size="small"
              disableCloseOnSelect
              limitTags={1}
              options={options}
              value={value}
              onChange={(_, newVal) => handleInlineFilterChange(key, newVal)}
              getOptionLabel={(o) => o?.label || o?.toString?.() || ''}
              isOptionEqualToValue={(opt, val) => (opt?.value ?? opt) === (val?.value ?? val)}
              slots={{ paper: FilterDropdownPaper }}
              slotProps={{
                paper: {
                  onClear: () => handleClearFilter(key),
                  hasSelection: value.length > 0,
                  sx: { borderRadius: '10px', minWidth: 200 },
                },
                listbox: { sx: { py: 0.5, maxHeight: 240, '& .MuiAutocomplete-option': { fontSize: '0.75rem', py: 0.5 } } },
              }}
              sx={dropdownSx}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={value.length === 0 ? placeholder : ''}
                  label={label}
                  slotProps={{
                    inputLabel: {
                      shrink: value.length > 0 || undefined,
                      sx: { fontSize: '0.72rem', fontWeight: 600 },
                    },
                  }}
                  variant="outlined"
                  size="small"
                />
              )}
              renderTags={(tagValues, getTagProps) => {
                const first = tagValues[0];
                const extra = tagValues.length - 1;
                return [
                  <Chip
                    {...getTagProps({ index: 0 })}
                    key={first?.value ?? 'tag-0'}
                    label={first?.label || first}
                    size="small"
                    sx={{
                      ...chipSx,
                      maxWidth: 'calc(100% - 44px)',
                    }}
                  />,
                  extra > 0 && (
                    <Box
                      key="more"
                      component="span"
                      sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#059669', flexShrink: 0, whiteSpace: 'nowrap', px: 0.25 }}
                    >
                      +{extra}
                    </Box>
                  ),
                ].filter(Boolean);
              }}
              renderOption={(optProps, option, { selected }) => {
                const { key, ...rest } = optProps;
                return (
                  <li key={key} {...rest} style={{ ...optProps.style, padding: '3px 8px' }}>
                    <Checkbox
                      size="small"
                      checked={selected}
                      sx={{ p: 0.25, mr: 0.5, color: '#d1d5db', '&.Mui-checked': { color: '#059669' } }}
                    />
                    <Typography sx={{ fontSize: '0.75rem' }}>{option?.label || option}</Typography>
                  </li>
                );
              }}
            />
          );
        })}

          {/* More Filters button */}
          <Tooltip
            title={moreFiltersCount > 0 ? `${moreFiltersCount} more filters active` : 'More filters'}
            placement="bottom"
          >
            <Button
              size="small"
              startIcon={<FilterListIcon sx={{ fontSize: 14 }} />}
              variant={moreFiltersCount > 0 ? 'contained' : 'outlined'}
              onClick={onFilterClick}
              sx={{
                height: '30px',
                px: 1,
                borderRadius: '6px',
                fontWeight: 600,
                fontSize: '0.72rem',
                textTransform: 'none',
                flexShrink: 0,
                whiteSpace: 'nowrap',
                ...(moreFiltersCount > 0
                  ? { bgcolor: '#0A6B3E', color: '#fff', '&:hover': { bgcolor: '#085D35' } }
                  : { borderColor: '#d1d5db', color: '#374151', '&:hover': { borderColor: '#0A6B3E', color: '#0A6B3E' } }),
              }}
            >
              {moreFiltersCount > 0 ? (
                <Badge badgeContent={moreFiltersCount} color="error" sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 15, minWidth: 15 } }}>
                  <span>More Filters</span>
                </Badge>
              ) : 'More Filters'}
            </Button>
          </Tooltip>

          {/* Save Search */}
          {onSaveSearchClick && canSaveSearch && (
            <Tooltip title="Save current filters as a bookmark" placement="bottom">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<BookmarkBorderIcon sx={{ fontSize: 12 }} />}
                  onClick={onSaveSearchClick}
                  aria-label="Save Search"
                  sx={{
                    height: 30, px: 1, fontSize: '0.72rem', fontWeight: 600,
                    borderRadius: '6px', textTransform: 'none', bgcolor: '#fff', flexShrink: 0,
                    whiteSpace: 'nowrap',
                    '& .MuiButton-startIcon': { mr: 0.4 },
                    '&:not(.Mui-disabled)': { borderColor: '#16a34a', color: '#16a34a', '&:hover': { borderColor: '#15803d', bgcolor: '#f0fdf4' } },
                  }}
                >
                  Save Search
                </Button>
              </span>
            </Tooltip>
          )}

          {/* Reset Filter */}
          {showResetFilterSlot && onResetFilters && (
            <Tooltip
              title={customPending && !hasAnyFilter ? 'Close custom Last Contacted and clear filters' : 'Clear all filters'}
              placement="bottom"
            >
              <Button
                size="small"
                variant="outlined"
                startIcon={<RestartAltIcon sx={{ fontSize: 12 }} />}
                onClick={handleResetFiltersClick}
                aria-label="Reset filters"
                sx={{
                  height: 30, px: 1, fontSize: '0.72rem', fontWeight: 600,
                  borderRadius: '6px', textTransform: 'none', border: '1px solid #fca5a5',
                  color: '#DC2626', bgcolor: '#fff', flexShrink: 0,
                  whiteSpace: 'nowrap',
                  '& .MuiButton-startIcon': { mr: 0.4 },
                  '&:hover': { bgcolor: '#FEF2F2', borderColor: '#DC2626' },
                }}
              >
                Reset Filter
              </Button>
            </Tooltip>
          )}
        </Box>

      </Box>

      {/* Row 3 — Search + Last Contacted + Reset Filter */}
      <Box
        sx={{
          px: 1.5,
          py: 0.75,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: '#F8FAFB',
          flexWrap: 'nowrap',
          overflowX: 'auto',
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
        }}
      >
        {/* Search bar */}
        <TextField
          size="small"
          placeholder="Search by Name, Email, Mobile"
          value={localSearch}
          onChange={handleSearchInput}
          onKeyDown={handleSearchKeyDown}
          sx={{
            flex: '0 1 400px',
            minWidth: 220,
            '& .MuiOutlinedInput-root': { height: '30px', borderRadius: '6px', bgcolor: '#fff' },
            '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0 },
            '& .MuiOutlinedInput-notchedOutline': { top: 0 },
            '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
          }}
          slotProps={{
            input: {
              notched: false,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                </InputAdornment>
              ),
            },
          }}
        />

        <Divider orientation="vertical" flexItem sx={{ borderColor: 'divider', alignSelf: 'stretch', my: 0.25 }} />

        {/* Last Contacted group */}
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'text.secondary', mr: 0.25, flexShrink: 0 }}>
          Last Contacted:
        </Typography>
        {LAST_CONTACTED_PRESETS.map(({ key, label }) => {
          const chipFilled = customPending ? key === 'custom' : activeLastContacted === key;
          return (
            <Chip
              key={key}
              label={label}
              size="small"
              onClick={() => handleLastContactedSelect(key)}
              variant={chipFilled ? 'filled' : 'outlined'}
              sx={{
                height: '26px',
                fontSize: '0.7rem',
                fontWeight: 600,
                borderRadius: '6px',
                cursor: 'pointer',
                ...(chipFilled
                  ? { bgcolor: '#0A6B3E', color: '#fff', borderColor: '#0A6B3E', '&:hover': { bgcolor: '#085D35' } }
                  : { borderColor: '#d1d5db', color: '#374151', '&:hover': { borderColor: '#0A6B3E', color: '#0A6B3E', bgcolor: '#ECFDF5' } }),
              }}
            />
          );
        })}

        {/* Custom date pickers */}
        {pendingPreset === 'custom' && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <DateTimePicker
                value={customStart}
                onChange={setCustomStart}
                slotProps={{ textField: { size: 'small', placeholder: 'Start', sx: { width: 165, '& .MuiInputBase-root': { fontSize: '0.7rem', height: 30, borderRadius: '6px' }, '& .MuiInputBase-input': { py: 0.5, px: 1 } } } }}
              />
              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>to</Typography>
              <DateTimePicker
                value={customEnd}
                onChange={setCustomEnd}
                slotProps={{ textField: { size: 'small', placeholder: 'End', sx: { width: 165, '& .MuiInputBase-root': { fontSize: '0.7rem', height: 30, borderRadius: '6px' }, '& .MuiInputBase-input': { py: 0.5, px: 1 } } } }}
              />
            </Box>
          </LocalizationProvider>
        )}

        {/* Apply (custom only) */}
        {customPending && (
          <Button
            size="small"
            variant="contained"
            disabled={!customStart || !customEnd}
            onClick={handleLastContactedApply}
            sx={{ height: 28, minWidth: 56, fontSize: '0.68rem', fontWeight: 600, borderRadius: '6px', textTransform: 'none', bgcolor: '#0A6B3E', '&:hover': { bgcolor: '#085D35' } }}
          >
            Apply
          </Button>
        )}

        {/* Pagination - right aligned in Row 3 */}
        <Box sx={{ flexShrink: 0, ml: 'auto' }}>
          <Pagination
            totalCount={paginationInfo.total_count || 0}
            currentPage={page}
            pageSize={rowsPerPage}
            totalPages={paginationInfo.total_pages || 1}
            hasNext={paginationInfo.has_next || false}
            hasPrevious={paginationInfo.has_previous || false}
            onPageChange={onPageChange}
            onPageSizeChange={onRowsPerPageChange}
          />
        </Box>

      </Box>
    </Box>
  );
};

export default LeadsToolbarV2;
